<template>
  <div class="modal fade show due-modal" aria-labelledby="dueCollectionDetailTitle">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="due-modal-header">
          <div>
            <h4 class="mb-0">{{ entry.contact_name }}</h4>
            <small class="header-subtitle">{{ headerSubtitle }}</small>
          </div>
          <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-1 mb-1">
            <div class="btn-group btn-group-sm">
              <button
                  type="button"
                  class="btn"
                  :class="mode === 'this_date' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="onSelectMode('this_date')"
              >This Date</button>
              <button
                  type="button"
                  class="btn"
                  :class="mode === 'up_to_today' ? 'btn-primary' : 'btn-outline-secondary'"
                  @click="onSelectMode('up_to_today')"
              >Up To Today</button>
            </div>
            <button type="button" class="btn btn-sm btn-primary" @click="onCollectAll">
              Collect All
            </button>
          </div>

          <div class="table-responsive">
            <table class="table table-sm table-bordered mb-0">
              <thead>
              <tr>
                <th>Invoice No</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Status</th>
                <th class="text-end">Total</th>
                <th class="text-end">Paid</th>
                <th class="text-end">Adjusted</th>
                <th class="text-end">Due</th>
                <th class="text-center">Action</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="invoice in invoices" :key="invoice.id">
                <td>
                  <span class="invoice-link" @click="onClickInvoice(invoice)">{{ invoice.bill_number }}</span>
                  <div v-if="invoice.business_name" class="business-note">{{ invoice.business_name }}</div>
                </td>
                <td>{{ invoice.date }}</td>
                <td>
                  <span v-if="invoice.due_date">{{ invoice.due_date }}</span>
                  <span v-else class="fallback-date">
                    {{ invoice.effective_due_date }}
                    <span class="fallback-flag" title="No due date on the invoice; invoice date used">~</span>
                  </span>
                  <div v-if="invoice.days_overdue > 0" class="overdue-note">{{ invoice.days_overdue }}d overdue</div>
                </td>
                <td>
                  <span class="status-badge" :style="badgeStyle(invoice.bucket)">{{ bucketLabel(invoice.bucket) }}</span>
                </td>
                <td class="text-end">{{ commaFormat(invoice.total_amount) }}</td>
                <td class="text-end">{{ commaFormat(invoice.paid_amount) }}</td>
                <td class="text-end">{{ commaFormat(invoice.adjusted_amount) }}</td>
                <td class="text-end fw-bold">{{ commaFormat(invoice.due_amount) }}</td>
                <td class="text-center">
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="onCollectInvoice(invoice)">
                    Collect
                  </button>
                </td>
              </tr>
              <tr v-if="!invoices.length && !loading">
                <td colspan="9" class="text-center py-3 empty-row">No due invoice found</td>
              </tr>
              </tbody>
              <tfoot v-if="invoices.length">
              <tr>
                <th colspan="7" class="text-end">Total Due</th>
                <th class="text-end">{{ commaFormat(totalDue) }}</th>
                <th></th>
              </tr>
              </tfoot>
            </table>
          </div>

          <div class="mt-1">
            <Pagination
                :offset="offset"
                state-name="secondPaginationData"
                @onPageChange="onPageChange"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="btn btn-outline-secondary">Close</button>
        </div>
      </div>
    </div>
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handlePurchase from '@/services/modules/purchase'
import figureFormatter from '@/services/utils/figureFormatter'
import { getDueCollectionStatusColor } from '@/services/utils/global'

const props = defineProps({
  entry: {
    type: Object,
    required: true
  }
})

const route = useRoute()
const router = useRouter()
const store = useStore()

const { fetchDueCollectionDayDetail } = handlePurchase()
const { commaFormat } = figureFormatter()

const loading = ref(false)
const invoices = ref([])
const totalDue = ref(0)
const offset = ref(15)
const parentPage = ref(route.query.page)
const mode = ref(resolveInitialMode())

const headerSubtitle = computed(() => {
  if (props.entry.contact_mobile_no) return props.entry.contact_mobile_no
  return 'No mobile number'
})

function badgeStyle(bucket) {
  const color = getDueCollectionStatusColor(bucket)
  return 'background:' + color.bg + ';border-color:' + color.border
}

function bucketLabel(bucket) {
  if (bucket === 'overdue') return 'Overdue'
  if (bucket === 'due_today') return 'Due Today'
  return 'Upcoming'
}

function resolveInitialMode() {
  if (props.entry.effective_due_date) return 'this_date'
  return 'up_to_today'
}

function getStartDate() {
  if (mode.value === 'this_date') return props.entry.effective_due_date
  return '1900-01-02'
}

function getEndDate() {
  if (mode.value === 'this_date') return props.entry.effective_due_date
  return todayKey()
}

function todayKey() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return now.getFullYear() + '-' + month + '-' + day
}

function getDetail() {
  let query = '?company_id=' + route.params.companyId
  query += '&contact_profile_id=' + props.entry.contact_profile_id
  query += '&start_date=' + getStartDate()
  query += '&end_date=' + getEndDate()
  query += '&offset=' + offset.value
  if (route.query.page) query += '&page=' + route.query.page

  loading.value = true
  return fetchDueCollectionDayDetail(query).then(function (res) {
    if (!res.status) {
      invoices.value = []
      totalDue.value = 0
      store.commit('resetPagination', 'secondPaginationData')
      return
    }
    invoices.value = res.data.invoices.data
    totalDue.value = res.data.total_due
    setPagination(res.data.invoices)
  }).finally(function () {
    loading.value = false
  })
}

function setPagination(data) {
  store.commit('setPaginationData', {
    stateName: 'secondPaginationData',
    data: {
      records: data.total,
      perPage: data.per_page,
      currentPage: data.current_page
    }
  })
}

async function onPageChange(page) {
  const query = Object.assign({}, route.query)
  query.page = page
  await router.replace({ path: route.path, query: query })
  getDetail()
}

async function resetPage() {
  if (!route.query.page) return
  const query = Object.assign({}, route.query)
  delete query.page
  await router.replace({ path: route.path, query: query })
}

function restoreParentPage() {
  const query = Object.assign({}, route.query)
  if (parentPage.value) query.page = parentPage.value
  if (!parentPage.value) delete query.page
  router.replace({ path: route.path, query: query })
}

async function onSelectMode(value) {
  if (mode.value === value) return
  mode.value = value
  await resetPage()
  getDetail()
}

function goToMoneyReceipt(saleMasterId) {
  const query = {
    contactId: props.entry.contact_profile_id,
    back_url: encodeURIComponent(route.fullPath)
  }
  if (saleMasterId) query.saleMasterId = saleMasterId

  closeModal()
  router.push({
    name: 'sales-invoice-receipt',
    params: route.params,
    query: query
  })
}

function onCollectInvoice(invoice) {
  goToMoneyReceipt(invoice.id)
}

function onClickInvoice(invoice) {
  const params = Object.assign({}, route.params)
  params.salesId = invoice.id

  const target = router.resolve({
    name: 'sales-invoice-print',
    params: params
  })
  window.open(target.href, '_blank')
}

function onCollectAll() {
  goToMoneyReceipt(null)
}

function closeModal() {
  restoreParentPage()
  store.commit('resetPagination', 'secondPaginationData')
  store.state.isModalOpenTwo = false
}

onMounted(async () => {
  store.commit('resetPagination', 'secondPaginationData')
  await resetPage()
  getDetail()
})
</script>

<style scoped>
.due-modal {
  display: block;
  background: rgba(16, 24, 40, .45);
  color: #000;
}

.due-modal .modal-content {
  border: 0;
  border-radius: 14px;
  box-shadow: 0 16px 48px rgba(16, 24, 40, .22);
}

.due-modal .modal-body {
  padding: 1.1rem 1.4rem;
}

.due-modal .modal-footer {
  padding: .9rem 1.4rem;
  border-top: 1px solid #f2f4f7;
}

.due-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.4rem .9rem;
  border-bottom: 1px solid #f2f4f7;
}

.due-modal-header h4 {
  color: #000;
  font-weight: 700;
  letter-spacing: -.01em;
}

.header-subtitle {
  color: #000;
  font-size: .85rem;
}

.due-modal .table {
  color: #000;
  font-size: .92rem;
}

.due-modal .table th,
.due-modal .table td {
  color: #000;
  padding: .6rem .75rem;
  vertical-align: middle;
}

.due-modal .table thead th {
  background: #fafbfd;
  font-size: .8rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  white-space: nowrap;
}

.due-modal .table tfoot th {
  background: #f7f8fc;
  font-weight: 700;
}

.due-modal .table tbody tr:hover td {
  background: #fafaff;
}

.empty-row {
  color: #000;
}

.status-badge {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 11px;
  border: 1px solid;
  font-size: .75rem;
  font-weight: 600;
  color: #000;
  white-space: nowrap;
}

.overdue-note {
  font-size: .75rem;
  color: #000;
  margin-top: 1px;
}

.invoice-link {
  color: #3b3fc4;
  font-weight: 600;
  cursor: pointer;
}

.invoice-link:hover {
  text-decoration: underline;
}

.business-note {
  font-size: .75rem;
  color: #000;
  margin-top: 1px;
}

.due-modal .btn-outline-secondary,
.due-modal .btn-outline-primary {
  color: #000;
  font-weight: 500;
}

.due-modal .btn-outline-secondary:hover,
.due-modal .btn-outline-secondary:focus,
.due-modal .btn-outline-secondary:active,
.due-modal .btn-outline-primary:hover,
.due-modal .btn-outline-primary:focus,
.due-modal .btn-outline-primary:active {
  color: #000;
  background-color: #f2f3f7;
}

.fallback-date {
  color: #000;
}

.fallback-flag {
  font-weight: 700;
  opacity: .7;
}
</style>
